import { BOARD_W, BOARD_H, R, CARET_H, DRONE_RADIUS, LANE_RADIUS, plotTileBox } from './geometry'
import { laneBox } from './lanes'
import { subCenter } from '../../../utils/hexGeometry'

// ─── The tutorial's gesture hand ─────────────────────────────────────────────
// A white cartoon glove that shows a gesture on the board, looping: its
// fingertip travels a path of board points (HexBoard.css animates it).
//   { kind: 'swipe', col }                a press low on the corridor's lane
//                                         and a swipe up it, a trail behind
//                                         the fingertip while it's pressed
//   { kind: 'tap', col }                  a tap on the corridor's red caret
//   { kind: 'move', from, to }            a drag from `from`'s front drone
//                                         into `to`'s next free slot
//   { kind: 'brood', col, centerS }       a drag from the corridor's second
//                                         drone onto its first, then down into
//                                         the Hive to the brood's spot
// Corridors are 0-based here. `selfBoard` says how full each corridor is.

// The glove's width, in hex radii.
const HAND_W = 4

const pct = ([x, y]) => [`${(x / BOARD_W) * 100}%`, `${(y / BOARD_H) * 100}%`]
const tile = (c, p) => { const b = plotTileBox('self', c, p); return [b.cx, b.cy] }
const units = (bf) => (bf?.armyQty || 0) + (bf?.newArmies || 0) + (bf?.movedInArmies || 0)

export function gesturePoints(hint, selfBoard, caps) {
    if (hint.kind === 'swipe' || hint.kind === 'tap') {
        const lane = laneBox('self', hint.col, caps[hint.col])
        if (hint.kind === 'tap') return [[lane.cx, lane.y - CARET_H * 0.5]]
        return [[lane.cx, lane.y + lane.h * 0.8], [lane.cx, lane.y + LANE_RADIUS]]
    }
    if (hint.kind === 'move') return [tile(hint.from, 0), tile(hint.to, units(selfBoard?.[hint.to]))]
    if (hint.kind === 'brood') {
        const { x, y } = subCenter('self', hint.centerS, R)
        return [tile(hint.col, 1), tile(hint.col, 0), [x, y]]
    }
    return null
}

// The glove: Phosphor Icons' hand-pointing (MIT, (c) 2023 Phosphor Icons,
// @phosphor-icons/core 2.1.1), the fill weight in white under the regular
// weight's black outline. Its fingertip is at (116, 16) of the 256 × 256 box.
const GLOVE_FILL = 'M224,104v50.93c0,46.2-36.85,84.55-83,85.06A83.71,83.71,0,0,1,80.6,215.4C58.79,192.33,34.15,136,34.15,136a16,16,0,0,1,6.53-22.23c7.66-4,17.1-.84,21.4,6.62l21,36.44a6.09,6.09,0,0,0,6,3.09l.12,0A8.19,8.19,0,0,0,96,151.74V32a16,16,0,0,1,16.77-16c8.61.4,15.23,7.82,15.23,16.43V104a8,8,0,0,0,8.53,8,8.17,8.17,0,0,0,7.47-8.25V88a16,16,0,0,1,16.77-16c8.61.4,15.23,7.82,15.23,16.43V112a8,8,0,0,0,8.53,8,8.17,8.17,0,0,0,7.47-8.25v-7.28c0-8.61,6.62-16,15.23-16.43A16,16,0,0,1,224,104Z'
const GLOVE_OUTLINE = 'M196,88a27.86,27.86,0,0,0-13.35,3.39A28,28,0,0,0,144,74.7V44a28,28,0,0,0-56,0v80l-3.82-6.13A28,28,0,0,0,35.73,146l4.67,8.23C74.81,214.89,89.05,240,136,240a88.1,88.1,0,0,0,88-88V116A28,28,0,0,0,196,88Zm12,64a72.08,72.08,0,0,1-72,72c-37.63,0-47.84-18-81.68-77.68l-4.69-8.27,0-.05A12,12,0,0,1,54,121.61a11.88,11.88,0,0,1,6-1.6,12,12,0,0,1,10.41,6,1.76,1.76,0,0,0,.14.23l18.67,30A8,8,0,0,0,104,152V44a12,12,0,0,1,24,0v68a8,8,0,0,0,16,0V100a12,12,0,0,1,24,0v20a8,8,0,0,0,16,0v-4a12,12,0,0,1,24,0Z'
// (Also the chest screen's tapping finger, prototype/ChestReveal.js.)
export function Glove() {
    return (
        <svg viewBox="0 0 256 256" width="100%" aria-hidden="true">
            <path d={GLOVE_FILL} fill="#fff" />
            <path d={GLOVE_OUTLINE} fill="#111" />
        </svg>
    )
}

export default function GestureHand({ hint, selfBoard, caps }) {
    const points = gesturePoints(hint, selfBoard, caps)
    if (!points) return null
    const vars = {}
    points.forEach((pt, i) => { const [x, y] = pct(pt); vars[`--gx${i}`] = x; vars[`--gy${i}`] = y })
    const handW = ((HAND_W * R) / BOARD_W) * 100
    return (
        <>
            {/* The swipe's trail: from where the finger pressed up to the
                fingertip, brightest at the finger, there only while pressed. */}
            {hint.kind === 'swipe' && (
                <div className="hexb__gesture-trail" aria-hidden="true" style={{
                    left: vars['--gx0'], top: vars['--gy1'],
                    height: `${((points[0][1] - points[1][1]) / BOARD_H) * 100}%`,
                    width: `${((1.1 * R) / BOARD_W) * 100}%`,
                }} />
            )}
            {/* The tap's ripple on the caret. */}
            {hint.kind === 'tap' && (
                <div className="hexb__gesture-ripple" aria-hidden="true"
                    style={{ left: vars['--gx0'], top: vars['--gy0'], width: `${((2.4 * R) / BOARD_W) * 100}%` }} />
            )}
            <div className={`hexb__gesture hexb__gesture--${hint.kind}`} data-gesture={hint.kind}
                style={{ ...vars, width: `${handW}%`, pointerEvents: 'none' }}>
                {/* The drone being carried, under the fingertip while it drags. */}
                {(hint.kind === 'move' || hint.kind === 'brood') && (
                    <div className="hexb__gesture-ghost" style={{ width: `${((2 * DRONE_RADIUS) / (HAND_W * R)) * 100}%` }} />
                )}
                {/* A glow under the fingertip: the finger is pressed. */}
                <div className="hexb__gesture-touch" />
                <div className="hexb__gesture-hand"><Glove /></div>
            </div>
        </>
    )
}
